import { CalendarDotsIcon } from "@phosphor-icons/react/dist/csr/CalendarDots";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import * as React from "react";
import { Button } from "@volca/ui/components/ui/button";
import { FilterChip } from "@volca/ui/components/ui/filter-chip";
import { PopoverTrigger } from "@volca/ui/components/ui/popover";

interface TimeRangeTriggerProps {
  id: string;
  labelId?: string;
  valueId: string;
  displayValue: string;
  hasValue: boolean;
  open: boolean;
  onClear: (e?: React.MouseEvent) => void;
  variant?: "field" | "filter";
  label?: string;
}

export const TimeRangeTrigger = React.forwardRef<
  HTMLButtonElement,
  TimeRangeTriggerProps
>(
  (
    {
      id,
      labelId,
      valueId,
      displayValue,
      hasValue,
      open,
      onClear,
      variant = "field",
      label = "Date",
    },
    ref,
  ) =>
    variant === "filter" ? (
      <PopoverTrigger
        ref={ref}
        id={id}
        render={
          <FilterChip
            label={label}
            value={hasValue ? displayValue : undefined}
            placeholder={displayValue}
            onClear={hasValue ? () => onClear() : undefined}
          />
        }
      />
    ) : (
      <div className="relative">
        <PopoverTrigger
          ref={ref}
          id={id}
          aria-labelledby={labelId ? `${labelId} ${valueId}` : undefined}
          render={
            <Button
              variant="outline"
              data-empty={!hasValue && !open}
              className="w-full justify-between font-normal data-[empty=true]:text-subtle"
            />
          }
        >
          <span id={valueId} className="truncate">
            {displayValue}
          </span>
          {/* The clear button sits over this space, beside the trigger
          rather than inside it: a button can't hold another. */}
          {hasValue ? (
            <span aria-hidden className="size-6 shrink-0" />
          ) : (
            <CalendarDotsIcon
              aria-hidden
              data-icon="inline-end"
              className="text-subtle"
            />
          )}
        </PopoverTrigger>
        {hasValue && (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Clear date range"
            className="absolute top-1/2 right-1 -translate-y-1/2"
            onClick={onClear}
          >
            <XIcon aria-hidden />
          </Button>
        )}
      </div>
    ),
);

TimeRangeTrigger.displayName = "TimeRangeTrigger";
